/**
 * Subscribe to ESA converse-status SSE for a conversation (feat-80).
 * Opens only while a converse turn is in flight (enabled) and conversationId
 * is a real id — not for the whole thread lifetime.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { openConverseStatusStream } from '../services/esaConverseStatusApi';

export interface ConverseStatusEvent {
  seq: number;
  message: string;
  phase?: string | null;
  agent_ukey?: string | null;
  client_seq?: string | null;
  terminal?: boolean;
  ts?: string;
  conversation_id?: string;
  tenant_ukey?: string;
}

export interface UseConverseStatusStreamOptions {
  /** When false, close the status stream (default false). */
  enabled?: boolean;
  /** When set, drop events from other turns (cross-turn backlog). */
  clientSeq?: string | null;
}

export function useConverseStatusStream(
  conversationId: string | null,
  options?: UseConverseStatusStreamOptions,
): {
  events: ConverseStatusEvent[];
  clear: () => void;
} {
  const enabled = options?.enabled ?? false;
  const clientSeq = options?.clientSeq ?? null;
  const [events, setEvents] = useState<ConverseStatusEvent[]>([]);
  const lastSeqRef = useRef<number | undefined>(undefined);
  const clientSeqRef = useRef<string | null>(clientSeq);
  clientSeqRef.current = clientSeq;

  const clear = useCallback(() => {
    setEvents([]);
    lastSeqRef.current = undefined;
  }, []);

  useEffect(() => {
    if (!enabled || !conversationId || conversationId.startsWith('prov-')) {
      setEvents([]);
      lastSeqRef.current = undefined;
      return undefined;
    }

    // Reset per open so after_seq / events never leak across turns or threads.
    setEvents([]);
    lastSeqRef.current = undefined;

    const subscription = openConverseStatusStream(conversationId, {
      afterSeq: undefined,
    });

    let cancelled = false;
    void (async () => {
      try {
        for await (const event of subscription.events()) {
          if (cancelled) return;
          if (event.event !== 'converse_status' && event.event !== 'message') {
            continue;
          }
          try {
            const data = JSON.parse(event.data) as ConverseStatusEvent;
            const filter = clientSeqRef.current;
            if (filter && data.client_seq != null && data.client_seq !== filter) {
              continue;
            }
            if (typeof data.seq === 'number') {
              lastSeqRef.current = data.seq;
            }
            setEvents(prev => [...prev, data]);
          } catch {
            // ignore malformed frames
          }
        }
      } catch {
        /* subscription closed or aborted */
      }
    })();

    return () => {
      cancelled = true;
      subscription.close();
    };
  }, [conversationId, enabled]);

  return { events, clear };
}
